/**
 * Tag categories and the tag filter shared by every view.
 *
 * Tags belong to categories. Two are built in: "Progress" (the mandatory
 * Todo / In Progress / Done) and "Labels" (every other tag; new tags go
 * there). A tag definition may name its category (`categoryId`); without one
 * (or with an unknown one) mandatory tags are Progress and others Labels, so
 * existing maps need no migration. More categories can be stored later in the
 * map metadata (`tagCategories`: [{ id, name }]) — the rule below already
 * handles any number of them, and a stored entry with a built-in id renames it.
 *
 * Each category also has a "Not tagged" entry (nodes carrying none of its
 * tags), with a synthetic id (see notTaggedId).
 *
 * The rule, for the eye (hidden ids) and the star (highlighted ids) alike:
 * within a category a node passes if one of its tags there is selected — or,
 * carrying none of them, if that category's "Not tagged" is selected; across
 * categories every category must pass (OR within, AND across). Categories
 * without any selection don't constrain. So hiding Done keeps everything else,
 * and hiding every label except "Assigned to me" (plus Not tagged) shows only
 * nodes assigned to me, whatever their progress.
 */

import { t } from "../../shared/i18n/index.js";

/** Filter id of the Progress category's "Not tagged" entry (kept from the
 *  former single "Untagged" entry so saved filters carry over). */
export const UNTAGGED_ID = "__untagged__";

export const PROGRESS_CATEGORY_ID = "progress";
export const LABELS_CATEGORY_ID = "labels";
/** Category new tags are created in. */
export const DEFAULT_TAG_CATEGORY_ID = LABELS_CATEGORY_ID;

// Built-in names are display texts (read when grouping), never stored.
export const BUILTIN_TAG_CATEGORIES = Object.freeze([
  Object.freeze({ id: PROGRESS_CATEGORY_ID, get name() { return t("views.tags.progress"); } }),
  Object.freeze({ id: LABELS_CATEGORY_ID, get name() { return t("views.tags.labels"); } }),
]);

// The mandatory Progress tags are stored with these English names (yjs-doc
// initDefaultTags); they are shown in the user's language while unrenamed.
const PROGRESS_TAG_LABELS = { "Todo": "views.tags.todo", "In Progress": "views.tags.inProgress", "Done": "views.tags.done" };

/** A tag definition's name as shown to the user (the stored name is unchanged). */
export function tagLabel(def) {
  const key = def?.mandatory ? PROGRESS_TAG_LABELS[def.name] : null;
  return key ? t(key) : def?.name ?? "";
}

/** Synthetic filter id of a category's "Not tagged" entry. */
export function notTaggedId(categoryId) {
  return categoryId === PROGRESS_CATEGORY_ID ? UNTAGGED_ID : `${UNTAGGED_ID}:${categoryId}`;
}

export function isNotTaggedId(id) {
  return typeof id === "string" && (id === UNTAGGED_ID || id.startsWith(`${UNTAGGED_ID}:`));
}

/** Tag ids referenced by a node (legacy string refs and { tagId } objects). */
export function nodeTagIds(node) {
  const ids = [];
  for (const t of node?.tags || []) {
    const id = typeof t === "string" ? t : t?.tagId;
    if (id) ids.push(id);
  }
  return ids;
}

/**
 * Categories in display order, each with its tag definitions.
 * @param {Array<{id: string, name: string, mandatory?: boolean, categoryId?: string}>} definitions
 * @param {Array<{id: string, name?: string}>} [storedCategories] - map metadata `tagCategories`
 * @returns {Array<{id: string, name: string, notTaggedId: string, tags: Array}>}
 */
export function groupTags(definitions = [], storedCategories = []) {
  const stored = Array.isArray(storedCategories) ? storedCategories.filter((c) => c && typeof c.id === "string") : [];
  const storedById = new Map(stored.map((c) => [c.id, c]));
  const categories = [
    ...BUILTIN_TAG_CATEGORIES.map((c) => ({ ...c, name: storedById.get(c.id)?.name || c.name })),
    ...stored.filter((c) => !BUILTIN_TAG_CATEGORIES.some((b) => b.id === c.id)).map((c) => ({ id: c.id, name: c.name || t("common.tags") })),
  ].map((c) => ({ ...c, notTaggedId: notTaggedId(c.id), tags: [] }));
  const byId = new Map(categories.map((c) => [c.id, c]));
  for (const def of definitions || []) {
    if (def?.id) byId.get(categoryIdOf(def, byId))?.tags.push(def);
  }
  return categories;
}

/** Category a tag definition belongs to (see file header for the fallback). */
export function categoryIdOf(def, knownCategories) {
  if (def?.categoryId && knownCategories?.has(def.categoryId)) return def.categoryId;
  return def?.mandatory ? PROGRESS_CATEGORY_ID : LABELS_CATEGORY_ID;
}

const LEGACY_STATUS_TAG = { todo: "Todo", "in-progress": "In Progress", done: "Done" };

/**
 * Build the filter for one render pass (per-node results are memoised on the
 * node objects of that pass).
 *
 * @param {object} opts
 * @param {Array} opts.definitions - tag definitions of the displayed map (the sidebar's)
 * @param {Array} [opts.categories] - stored categories of that map
 * @param {(node: object) => Array} [opts.definitionsFor] - definitions of the map a
 *   node really belongs to (linked maps have their own tag ids). Their
 *   Progress tags count as the displayed map's tag of the same name; their
 *   other tags are unknown here and ignored.
 */
export function createTagFilter({ definitions = [], categories = [], definitionsFor = null } = {}) {
  const groups = groupTags(definitions, categories);
  const categoryOfTag = new Map();
  const progressByName = new Map();
  for (const group of groups) {
    for (const def of group.tags) {
      categoryOfTag.set(def.id, group.id);
      if (def.mandatory) progressByName.set(def.name, def.id);
    }
  }
  const notTagged = new Map(groups.map((g) => [g.id, g.notTaggedId]));
  const memo = new WeakMap();

  // categoryId -> Set of the displayed map's tag ids the node carries there
  function tagsByCategory(node) {
    let result = memo.get(node);
    if (result) return result;
    result = new Map();
    const add = (id) => {
      const categoryId = categoryOfTag.get(id);
      if (!categoryId) return;
      if (!result.has(categoryId)) result.set(categoryId, new Set());
      result.get(categoryId).add(id);
    };
    let foreign = null;
    for (const id of nodeTagIds(node)) {
      if (categoryOfTag.has(id)) { add(id); continue; }
      if (!definitionsFor) continue;
      foreign ??= new Map((definitionsFor(node) || []).map((d) => [d.id, d]));
      const def = foreign.get(id);
      if (def?.mandatory && progressByName.has(def.name)) add(progressByName.get(def.name));
    }
    // Old imports keep a plain `status` until they get tags.
    if (!result.has(PROGRESS_CATEGORY_ID) && LEGACY_STATUS_TAG[node?.status]) {
      const id = progressByName.get(LEGACY_STATUS_TAG[node.status]);
      if (id) add(id);
    }
    memo.set(node, result);
    return result;
  }

  // Does the node pass category `categoryId` for the selected ids?
  function passes(node, categoryId, selected) {
    const own = tagsByCategory(node).get(categoryId);
    if (own?.size) {
      for (const id of own) if (selected.has(id)) return true;
      return false;
    }
    return selected.has(notTagged.get(categoryId));
  }

  // Categories that have at least one of `ids` selected.
  function constrained(ids) {
    const set = new Set(ids);
    const out = [];
    for (const group of groups) {
      if (set.has(group.notTaggedId) || group.tags.some((t) => set.has(t.id))) out.push(group.id);
    }
    return { set, categories: out };
  }

  let hiddenKey = null;
  let hiddenState = null;
  let matchKey = null;
  let matchState = null;

  return {
    groups,
    /** Whether the eye toggles hide this node by itself. */
    isHidden(node, hiddenIds) {
      if (!hiddenIds?.length || !node) return false;
      if (hiddenIds !== hiddenKey) {
        hiddenKey = hiddenIds;
        const { set, categories: cats } = constrained(hiddenIds);
        // A category passes when the node has a SHOWN tag there.
        const shown = new Set();
        for (const group of groups) {
          if (!set.has(group.notTaggedId)) shown.add(group.notTaggedId);
          for (const t of group.tags) if (!set.has(t.id)) shown.add(t.id);
        }
        hiddenState = { shown, categories: cats };
      }
      return hiddenState.categories.some((c) => !passes(node, c, hiddenState.shown));
    },
    /** Whether the node matches the highlighted (starred) tags. */
    isMatch(node, highlightedIds) {
      if (!highlightedIds?.length || !node) return false;
      if (highlightedIds !== matchKey) {
        matchKey = highlightedIds;
        matchState = constrained(highlightedIds);
      }
      const { set, categories: cats } = matchState;
      return cats.length > 0 && cats.every((c) => passes(node, c, set));
    },
  };
}
